import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, SkipBack, SkipForward, ShoppingCart, X } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { Waveform } from "@/components/Waveform";
import { fileUrl } from "@/lib/api";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const MiniPlayer = () => {
  const { current, playing, toggle, next, prev, progress, duration, seek, volume, setVolume, openLicense, queue } = usePlayer();

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ y: 110 }}
          animate={{ y: 0 }}
          exit={{ y: 110 }}
          transition={{ type: "spring", damping: 26, stiffness: 240 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-emerald-300/20 bg-zinc-950/95 p-3 backdrop-blur-2xl md:p-4"
          data-testid="persistent-mini-player"
        >
          <div className="mx-auto flex max-w-7xl items-center gap-3 md:gap-5">
            <img
              src={fileUrl(current.cover_url)}
              alt={current.title}
              className="h-11 w-11 rounded-lg object-cover md:h-14 md:w-14"
              data-testid="mini-player-thumbnail"
            />
            <div className="min-w-0 w-32 md:w-44">
              <p className="truncate text-sm font-semibold text-white" data-testid="mini-player-title">{current.title}</p>
              <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
                {current.bpm} BPM · {current.musical_key}
              </p>
            </div>

            <div className="flex items-center gap-1 md:gap-2">
              <button data-testid="mini-player-prev" onClick={prev} disabled={queue.length < 2}
                className="rounded-full p-2 text-zinc-400 transition-colors hover:text-white disabled:opacity-30">
                <SkipBack size={17} />
              </button>
              <button
                data-testid="persistent-mini-player-play-pause"
                onClick={toggle}
                className="rounded-full bg-emerald-300 p-2.5 text-black transition-transform hover:scale-105"
              >
                {playing ? <Pause size={17} fill="black" /> : <Play size={17} fill="black" className="ml-0.5" />}
              </button>
              <button data-testid="mini-player-next" onClick={next} disabled={queue.length < 2}
                className="rounded-full p-2 text-zinc-400 transition-colors hover:text-white disabled:opacity-30">
                <SkipForward size={17} />
              </button>
            </div>

            <div className="hidden flex-1 items-center gap-3 sm:flex">
              <span className="font-mono text-[11px] text-zinc-500" data-testid="mini-player-time">{fmt(progress)}</span>
              <Waveform
                seed={current.id}
                progress={duration ? progress / duration : 0}
                onSeek={(f) => seek(f * duration)}
                height={34}
                live={playing}
              />
              <span className="font-mono text-[11px] text-zinc-500">{fmt(duration)}</span>
            </div>

            <input
              type="range" min="0" max="1" step="0.01" value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="vol hidden w-20 lg:block"
              data-testid="mini-player-volume"
            />
            <button
              data-testid="mini-player-buy-button"
              onClick={() => openLicense(current)}
              className="hidden items-center gap-2 rounded-full bg-emerald-300/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300 hover:text-black md:flex"
            >
              <ShoppingCart size={14} /> Lease
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
